import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { JobForm, JobFormData } from '@/components/JobForm';
import { useCreateJob } from '@/hooks/useCreateJob';
import { chatApi } from '@/lib/chat-api';
import { pipelineApi } from '@/lib/pipeline-api';
import { ChatSession, JobData } from '@/types/chat';
import { MessageSquare, FileText, Clock, ArrowRight } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';

export default function CreateJobPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { createJob, isLoading } = useCreateJob();
  const [pendingSessions, setPendingSessions] = useState<ChatSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);

  // Check if we received jobData from chat (mode switch: chat → form)
  const initialJobData = (location.state as { jobData?: JobData; chatSessionId?: string })?.jobData;
  const chatSessionId = (location.state as { chatSessionId?: string })?.chatSessionId;

  // Fetch pending (active) sessions on mount
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const sessions = await chatApi.listSessions('active');
        setPendingSessions(sessions);
      } catch (err) {
        console.error('Failed to load pending sessions:', err);
      } finally {
        setLoadingSessions(false);
      }
    };
    fetchSessions();
  }, []);

  const handleSubmit = async (data: JobFormData) => {
    try {
      // JobForm validates openedAt/expiresAt before calling onSubmit, so they're guaranteed to exist
      const createdJob = await createJob({
        ...data,
        openedAt: data.openedAt || new Date().toISOString(),
        expiresAt: data.expiresAt || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      });
      // Save pipeline config after job creation
      if (data.pipelineConfig && createdJob.jobId) {
        try {
          await pipelineApi.updatePipelineConfig(createdJob.jobId, data.pipelineConfig);
        } catch {
          // Non-blocking — job was created, pipeline config save failed
          toast.error('Vaga criada, mas houve erro ao salvar o pipeline. Edite a vaga para configurar.');
        }
      }
      toast.success('Vaga criada com sucesso!');
      navigate('/jobs');
    } catch (error: any) {
      console.error('Failed to create job:', error);
      const msg = error?.message || '';
      const code = error?.code || '';
      if (code === 'QUOTA_EXCEEDED' || msg.includes('limite') || msg.includes('upgrade')) {
        toast.error(msg || 'Você atingiu o limite de vagas do plano gratuito. Acesse Configurações > Assinatura para fazer upgrade.', { duration: 8000 });
      } else {
        toast.error('Erro ao criar vaga. Tente novamente.');
      }
    }
  };

  // Mode switch: form → chat (resume existing session if available)
  const handleContinueWithAI = (formData?: JobFormData) => {
    if (chatSessionId) {
      navigate(`/jobs/create/chat/${chatSessionId}`, { state: { initialJobData: formData } });
    } else {
      navigate('/jobs/create/chat', { state: { initialJobData: formData } });
    }
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}min atrás`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h atrás`;
    const days = Math.floor(hours / 24);
    return `${days}d atrás`;
  };

  return (
    <AppLayout
      actions={
        <Button variant="outline" onClick={() => navigate('/jobs')}>
          Voltar
        </Button>
      }
    >
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Nova Vaga</h2>
          <p className="text-muted-foreground">
            Preencha as informações para criar uma nova vaga
          </p>
        </div>

        {/* Pending Sessions */}
        {!loadingSessions && pendingSessions.length > 0 && (
          <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Sessões em andamento
            </h3>
            <div className="space-y-2">
              {pendingSessions.map((session) => (
                <button
                  key={session.sessionId}
                  onClick={() => navigate(`/jobs/create/chat/${session.sessionId}`)}
                  className="w-full flex items-center justify-between p-3 bg-white rounded-md border border-blue-100 hover:border-blue-300 hover:bg-blue-50/50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <MessageSquare className="h-4 w-4 text-blue-600 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {session.jobData?.title || 'Vaga sem título'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatTimeAgo(session.lastActivityAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-blue-600 shrink-0">
                    <span className="text-xs">Retomar</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Option Selector */}
        <div className="mb-8 grid grid-cols-2 gap-4">
          <button
            onClick={() => {/* Already on form */}}
            className="p-6 border-2 border-primary bg-primary/5 rounded-lg text-left hover:bg-primary/10 transition-colors"
          >
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Formulário</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Preencha os campos manualmente
            </p>
          </button>
          
          <button
            onClick={() => handleContinueWithAI()}
            className="p-6 border-2 border-gray-200 rounded-lg text-left hover:border-primary hover:bg-primary/5 transition-colors"
          >
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Chat IA</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Crie a vaga conversando com IA
            </p>
          </button>
        </div>

        <JobForm
          onSubmit={handleSubmit}
          isLoading={isLoading}
          initialJobData={initialJobData}
          onContinueWithAI={handleContinueWithAI}
        />
      </div>
    </AppLayout>
  );
}
